import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DriverBackgroundCheckApiService } from './driver-background-check-api.service';
import { DriverBackgroundCheckStateService } from './driver-background-check-state.service';
import { T2LoadingService } from '../../trip2-theme/components/t2-loading/t2-loading.service';
import { SweetAlertHelper } from '../../core/helpers/sweet-alert-helper.service';
import { Subscription } from 'rxjs/Subscription';
import { _downloadFile } from '../trip2-query-builder/utils/helpers';
import {
  IUnifiedBackgroundCheckDateItem,
  IUnifiedBackgroundCheckDetail,
  IUnifiedMotorVehicleReport,
  IUnifiedMvrViolation,
  IUnifiedDocument,
  UnifiedBackgroundCheckSource
} from '../../core/interfaces/unified-background-check.interface';
import {
  IRecordCard,
  ISidebarSection,
  IMetric,
  ISubjectMatch,
  IReviewItem,
  IReportDetails,
  IBackgroundCheckViewModel,
  IDriversLicenseGroup,
  IIdentityViewModel,
  BannerState
} from './driver-background-check.view-models';
import { BackgroundCheckCardBuilder } from './background-check-card.builder';
import { DEACTIVATE_REASON_MAX_LENGTH, SECTION_AVAILABILITY } from './driver-background-check.constants';
import { UrlHelper } from 'app/core/helpers/uri.helper';

@Component({
  selector: 'app-driver-background-check',
  templateUrl: './driver-background-check.component.html',
  styleUrls: ['./driver-background-check.component.scss']
})
export class DriverBackgroundCheckComponent implements OnInit, OnDestroy {
  form: FormGroup = null;
  reports: IUnifiedBackgroundCheckDateItem[] = [];
  selectedReport: IUnifiedBackgroundCheckDateItem = null;
  selectedDetail: IUnifiedBackgroundCheckDetail = null;
  showReportDate = true;
  noReportsFound = false;
  selectedSection = 'summary';
  isExpanded = false;

  deactivatePendingReport: IUnifiedBackgroundCheckDateItem = null;
  deactivateError = '';

  toast: string = null;
  private toastTimer: any = null;

  sidebarSections: ISidebarSection[] = [];

  criminalCards: IRecordCard[] = [];
  driversLicenseCards: IRecordCard[] = [];
  sharedSsnCards: IRecordCard[] = [];
  professionalLicenseCards: IRecordCard[] = [];
  watchListCards: IRecordCard[] = [];
  mvrViolationCards: IRecordCard[] = [];
  mvrAccidentCards: IRecordCard[] = [];
  metrics: IMetric[] = [];
  itemsRequiringReview: IReviewItem[] = [];
  documents: IUnifiedDocument[] = [];
  driversLicenseGroups: IDriversLicenseGroup[] = [];
  subjectMatch: ISubjectMatch = { name: '—', ssn: '', dob: '—', driverLicense: '', email: '', phone: '', result: '—', assessment: '—' };
  identity: IIdentityViewModel = {
    names: [], dobs: [], addresses: [], phones: [], lastPhone: undefined, emails: [],
    ssnTrace: '—', ssnHistory: [], isClear: true, individualsSharingSsn: [],
  };

  private subscriptions: Subscription[] = [];
  private driverId: number = null;

  private reportDetailsCache: IReportDetails = null;
  private reportDetailsCacheDetail: IUnifiedBackgroundCheckDetail = null;
  private reportDetailsCacheReport: IUnifiedBackgroundCheckDateItem = null;
  private inactiveTooltipCache: string = null;
  private inactiveTooltipCacheDetail: IUnifiedBackgroundCheckDetail = null;

  constructor(
    private fb: FormBuilder,
    private apiService: DriverBackgroundCheckApiService,
    private stateService: DriverBackgroundCheckStateService,
    private _loading: T2LoadingService,
    private sweetAlertHelper: SweetAlertHelper
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.subscribeToDriverId();
    this.subscribeToExpanded();
    this.subscribeToMaskPIIActive();
    this.subscribeToChromeActions();
    this.resetSidebar();
  }

  changeActiveState(): void {
    if (!this.selectedReport) {
      return;
    }
    if (this.form.get('active').value) {
      this.setReportActiveState(true);
    } else if (this.selectedReport.source === 'SearchBug') {
      this.openDeactivateModal();
    } else {
      this.setReportActiveState(false);
    }
  }

  private openDeactivateModal(): void {
    this.deactivatePendingReport = this.selectedReport;
    this.deactivateError = '';
  }

  onCancelDeactivate(): void {
    this.deactivatePendingReport = null;
    this.deactivateError = '';
    this.form.get('active').setValue(true, { emitEvent: false });
  }

  onConfirmDeactivate(reasonText: string): void {
    const missing: string[] = [];
    if (!reasonText || !reasonText.trim()) {
      missing.push('Reason');
    }
    if (missing.length) {
      this.deactivateError = missing.join(', ') + (missing.length === 1 ? ' is' : ' are') + ' required.';
      return;
    }
    if (reasonText.length > DEACTIVATE_REASON_MAX_LENGTH) {
      this.deactivateError = `Reason must be ${DEACTIVATE_REASON_MAX_LENGTH} characters or less.`;
      return;
    }
    this.deactivatePendingReport = null;
    this.setReportActiveState(false, reasonText.trim());
  }

  showToast(message: string): void {
    clearTimeout(this.toastTimer);
    this.toast = message;
    this.toastTimer = setTimeout(() => {
      this.toast = null;
    }, 2600);
  }

  get bannerState(): BannerState | null {
    if (this.noReportsFound) {
      return 'no-reports';
    }
    if (this.isPending) {
      return 'pending';
    }
    if (this.isError) {
      return 'error';
    }
    return null;
  }

  download(): void {
    if (!this.selectedReport || !this.selectedReport.nativeid) {
      this.sweetAlertHelper.captureException('Report not available');
      return;
    }
    const { source, nativeid } = this.selectedReport;
    this._loading.show('Downloading Report');
    this.subscriptions.push(
      this.apiService
        .download(source, nativeid)
        .finally(() => {
          this._loading.hiden();
        })
        .subscribe({
          next: (data) => {
            if (data.source === 'CheckR') {
              const document = data.checkrresult;
              if (document && document.downloaduri && UrlHelper.isSafeHttpUrl(document.downloaduri)) {
                window.open(document.downloaduri, '_blank');
              } else {
                this.sweetAlertHelper.captureException('Document not available for this report');
              }
              return;
            }
            if (data.source === 'SearchBug') {
              const searchBugData = data.searchbugresult;
              const payload = (searchBugData && searchBugData.combinedjson) || JSON.stringify(searchBugData);
              const blob = new Blob([payload], { type: 'application/json' });
              _downloadFile(blob, `report_${nativeid}.json`);
            }
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          },
        })
    );
  }

  loadReportDetail(report: IUnifiedBackgroundCheckDateItem): void {
    if (!report || !report.nativeid) {
      return;
    }
    const loadingLabel = report.source === 'CheckR' ? 'Retrieving Live CheckR Report…' : 'Loading Report Detail';
    this._loading.show(loadingLabel);
    this.subscriptions.push(
      this.apiService
        .getReportDetail(report.source, report.nativeid)
        .finally(() => {
          this._loading.hiden();
        })
        .subscribe({
          next: (data) => {
            if (this.selectedReport !== report) {
              return;
            }
            if (!data) {
              this.sweetAlertHelper.captureException('Report detail not available');
              return;
            }
            this.selectedDetail = Object.assign({}, data);
            if (report.source === 'CheckR') {
              this.selectedDetail.isactive = report.isactive;
              this.selectedDetail.driverid = report.driverid;
              this.selectedDetail.drivercheckrid = report.drivercheckrid;
            }
            this.form.get('active').setValue(this.selectedDetail.isactive, { emitEvent: false });
            this.stateService.setMaskPIIActive(true);
            this.applyViewModel(new BackgroundCheckCardBuilder(this.selectedDetail, this.source).build());
            this.selectedSection = 'summary';
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          },
        })
    );
  }

  private applyViewModel(viewModel: IBackgroundCheckViewModel): void {
    this.criminalCards = viewModel.criminalCards;
    this.driversLicenseCards = viewModel.driversLicenseCards;
    this.driversLicenseGroups = viewModel.driversLicenseGroups;
    this.sharedSsnCards = viewModel.sharedSsnCards;
    this.professionalLicenseCards = viewModel.professionalLicenseCards;
    this.watchListCards = viewModel.watchListCards;
    this.mvrViolationCards = viewModel.mvrViolationCards;
    this.mvrAccidentCards = viewModel.mvrAccidentCards;
    this.documents = viewModel.documents;
    this.metrics = viewModel.metrics;
    this.itemsRequiringReview = viewModel.itemsRequiringReview;
    this.subjectMatch = viewModel.subjectMatch;
    this.identity = viewModel.identity;
    this.sidebarSections = viewModel.sidebarSections;
  }

  loadReports(): void {
    if (!this.driverId) {
      return;
    }
    this._loading.show();
    this.subscriptions.push(
      this.apiService
        .getReportDates(this.driverId)
        .finally(() => {
          this._loading.hiden();
        })
        .subscribe({
          next: (data) => {
            this.loadData(data || []);
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          },
        })
    );
  }

  onReportDateChange(index: string): void {
    if (!index) {
      this.clearSelectedReport();
      return;
    }
    const i = Number(index);
    const report = this.reports[i];
    if (report) {
      this.selectedReport = report;
      this.loadReportDetail(report);
    } else {
      this.clearSelectedReport();
    }
  }

  runCheck(): void {
    if (!this.driverId) {
      return;
    }
    this._loading.show('Loading Background Check');
    this.subscriptions.push(
      this.apiService
        .runCheck(this.driverId)
        .finally(() => {
          this._loading.hiden();
        })
        .subscribe({
          next: () => {
            this.showToast('Successful');
            this.loadReports();
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          },
        })
    );
  }

  selectSection(sectionId: string): void {
    this.selectedSection = sectionId;
  }

  get isMaskPIIActive(): boolean {
    return this.form && this.form.get('maskPII').value;
  }

  get piiToggleLabel(): string {
    return this.isMaskPIIActive ? 'Reveal PII' : 'Hide PII';
  }

  get source(): UnifiedBackgroundCheckSource | null {
    return this.selectedDetail ? this.selectedDetail.source : null;
  }

  get ssnPreMasked(): boolean {
    return !!(this.selectedDetail && this.selectedDetail.ssnispremasked);
  }

  get criminalCardsOpenByDefault(): boolean {
    return this.source !== 'SearchBug';
  }

  get sectionNote(): string {
    switch (this.selectedSection) {
      case 'summary':
        return `${this.sidebarSections.length} section(s) available for this report`;
      case 'identity':
        return `${this.identity.individualsSharingSsn.length} shared SSN individual(s) found`;
      case 'licenses':
        return `${this.getSectionCount('licenses')} driver's license record(s) found`;
      case 'permits':
        return `${this.getSectionCount('permits')} professional license record(s) found`;
      case 'mvr':
        return `${this.mvrViolations.length} violation(s), ${this.mvrAccidents.length} accident(s) found`;
      case 'criminal':
        return `${this.getSectionCount('criminal')} criminal record(s) found`;
      case 'watchlist':
        return `${this.getSectionCount('watchlist')} watch list record(s) found`;
      case 'documents':
        return `${this.getSectionCount('documents')} document(s) found`;
      case 'details':
        return 'Report metadata and provider details';
      default:
        return '';
    }
  }

  get motorVehicleReport(): IUnifiedMotorVehicleReport {
    return (this.selectedDetail && this.selectedDetail.motorvehiclereport) || null;
  }

  get mvrViolations(): IUnifiedMvrViolation[] {
    return (this.motorVehicleReport && this.motorVehicleReport.violations) || [];
  }

  get mvrAccidents(): IUnifiedMvrViolation[] {
    return (this.motorVehicleReport && this.motorVehicleReport.accidents) || [];
  }

  get reportDetails(): IReportDetails {
    if (this.selectedDetail !== this.reportDetailsCacheDetail || this.selectedReport !== this.reportDetailsCacheReport) {
      this.reportDetailsCacheDetail = this.selectedDetail;
      this.reportDetailsCacheReport = this.selectedReport;
      this.reportDetailsCache = {
        reportToken: this.selectedDetail && this.selectedDetail.reporttoken,
        searchType: this.source === 'CheckR' ? 'CheckR Background Check' : 'Comprehensive Person Report',
        reportDate: (this.selectedDetail && this.selectedDetail.reportdate) || (this.selectedReport && this.selectedReport.date),
        source: this.source === 'CheckR' ? 'CheckR API' : 'SearchBug API',
        status: this.selectedDetail && this.selectedDetail.status,
        result: this.selectedDetail && this.selectedDetail.result,
        assessment: this.selectedDetail && this.selectedDetail.assessment,
        completedAt: (this.selectedDetail && this.selectedDetail.reportdate) || (this.selectedReport && this.selectedReport.date),
      };
    }
    return this.reportDetailsCache;
  }

  get disabledButtonRunCheck(): boolean {
    if (!this.reports || !this.reports.length) {
      return false;
    }

    const [firstPosition] = this.reports;
    if (firstPosition.status === 'PENDING') {
      return true;
    }

    return this.reports.some((report) =>
      report.isactive &&
      this.isWithinLastYear(report.date) &&
      report.status !== 'ERROR'
    );
  }

  get disabledButtonDownloadReport(): boolean {
    return this.source !== 'CheckR' || !this.selectedDetail || this.selectedDetail.status !== 'COMPLETE';
  }

  get hasReports(): boolean {
    return !!(this.reports && this.reports.length);
  }

  get hasSelectedDetail(): boolean {
    return !!this.selectedDetail;
  }

  get isComplete(): boolean {
    return !!(this.selectedDetail && this.selectedDetail.status === 'COMPLETE');
  }

  get isPending(): boolean {
    return !!(this.selectedDetail && this.selectedDetail.status === 'PENDING');
  }

  get isError(): boolean {
    return !!(this.selectedDetail && this.selectedDetail.status === 'ERROR');
  }

  get inactiveTooltip(): string {
    if (this.selectedDetail !== this.inactiveTooltipCacheDetail) {
      this.inactiveTooltipCacheDetail = this.selectedDetail;
      this.inactiveTooltipCache = this.computeInactiveTooltip();
    }
    return this.inactiveTooltipCache;
  }

  private computeInactiveTooltip(): string {
    if (!this.selectedDetail || !this.selectedDetail.deactivatedon) {
      return 'Deactivated';
    }
    const datepipe = new DatePipe('en-US');
    const formattedDate = datepipe.transform(this.selectedDetail.deactivatedon, 'MM/dd/yyyy hh:mm a');
    const reason = this.selectedDetail.deactivationreason;
    if (reason) {
      return `Deactivated ${formattedDate}: ${reason}`;
    }
    return `Deactivated ${formattedDate}`;
  }

  get turnaroundTime(): string {
    const duration = this.selectedDetail && this.selectedDetail.totaldurationms;
    if (duration === null || duration === undefined) {
      return '—';
    }
    return this.formatDuration(duration);
  }

  private formatDuration(ms: number): string {
    if (!ms || ms <= 0) {
      return '0s';
    }
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }
    const totalMinutes = Math.floor(totalSeconds / 60);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (days > 0 || hours > 0) {
      parts.push(`${hours}h`);
    }
    parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  private getSectionCount(sectionId: string): number {
    const section = this.sidebarSections.find((s) => s.id === sectionId);
    return section ? section.count : 0;
  }

  private setReportActiveState(active: boolean, reason?: string): void {
    const { source, nativeid, drivercheckrid } = this.selectedReport;
    const request = active
      ? this.apiService.activate(source, this.driverId, { nativeid, drivercheckrid })
      : this.apiService.deactivate(source, this.driverId, { nativeid, drivercheckrid, reason });
    this._loading.show(active ? 'Activating Report' : 'Deactivating Report');
    this.subscriptions.push(
      request
        .finally(() => {
          this._loading.hiden();
        })
        .subscribe({
          next: (resp) => {
            this.showSuccessAndReload(resp && resp.message);
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
            this.form.get('active').setValue(!this.form.get('active').value, { emitEvent: false });
          },
        })
    );
  }

  private buildForm(): void {
    this.form = this.fb.group({
      report: [null],
      active: [false],
      maskPII: [true],
    });
  }

  private loadData(data: IUnifiedBackgroundCheckDateItem[]): void {
    this.reports = data;
    if (this.reports && this.reports.length) {
      this.noReportsFound = false;
      this.selectedReport = this.reports[0];
      this.form.get('report').setValue(0, { emitEvent: false });
      this.loadReportDetail(this.selectedReport);
    } else {
      this.noReportsFound = true;
      this.clearSelectedReport();
    }
  }

  private clearSelectedReport(): void {
    this.selectedReport = null;
    this.selectedDetail = null;
    this.resetRecordCards();
    this.resetSidebar();
    this.selectedSection = 'summary';
    this.form.get('report').setValue(null, { emitEvent: false });
    this.form.get('active').setValue(false, { emitEvent: false });
  }

  private resetSidebar(): void {
    const sections: ISidebarSection[] = [
      { id: 'summary', label: 'Summary', count: 0, findings: 0, icon: 'fa-clipboard-list', badgeType: 'none' },
      { id: 'identity', label: 'Identity & SSN Trace', count: 0, findings: 0, icon: 'fa-id-card', badgeType: 'none' },
      { id: 'licenses', label: 'Driver\'s Licenses', count: 0, findings: 0, icon: 'fa-address-card', badgeType: 'none' },
      { id: 'mvr', label: 'Motor Vehicle Report', count: 0, findings: 0, icon: 'fa-car', badgeType: 'none' },
      { id: 'criminal', label: 'Criminal Background Check', count: 0, findings: 0, icon: 'fa-gavel', badgeType: 'none' },
      { id: 'watchlist', label: 'Watch List', count: 0, findings: 0, icon: 'fa-user-shield', badgeType: 'none' },
      { id: 'documents', label: 'Documents', count: 0, findings: 0, icon: 'fa-file-pdf', badgeType: 'none' },
      { id: 'permits', label: 'Licenses & Permits', count: 0, findings: 0, icon: 'fa-id-badge', badgeType: 'none' },
      { id: 'details', label: 'Report Details', count: 0, findings: 0, icon: 'fa-info-circle', badgeType: 'none' },
    ];
    this.sidebarSections = sections.filter((s) => this.isSectionAvailable(s.id));
  }

  private resetRecordCards(): void {
    this.criminalCards = [];
    this.driversLicenseCards = [];
    this.sharedSsnCards = [];
    this.professionalLicenseCards = [];
    this.watchListCards = [];
    this.mvrViolationCards = [];
    this.mvrAccidentCards = [];
  }

  private isSectionAvailable(id: string): boolean {
    const allowed = SECTION_AVAILABILITY[id];
    return !allowed || !this.source || allowed.indexOf(this.source) !== -1;
  }

  private isWithinLastYear(dateStr: string): boolean {
    if (!dateStr) {
      return false;
    }
    const reportDate = new Date(dateStr);
    if (isNaN(reportDate.getTime())) {
      return false;
    }
    const oneYearMs = 365 * 24 * 60 * 60 * 1000;
    const diffMs = Date.now() - reportDate.getTime();
    return diffMs >= 0 && diffMs <= oneYearMs;
  }

  private showSuccessAndReload(message: string): void {
    this.showToast(message || 'Success');
    this.loadReports();
  }

  private subscribeToDriverId(): void {
    this.subscriptions.push(
      this.stateService.driverId$.subscribe({
        next: (value) => {
          if (value) {
            this.driverId = value;
            this.loadReports();
          }
        },
      })
    );
  }

  private subscribeToExpanded(): void {
    this.subscriptions.push(
      this.stateService.expanded$.subscribe({
        next: (value) => {
          this.isExpanded = value;
        },
      })
    );
  }

  private subscribeToMaskPIIActive(): void {
    this.subscriptions.push(
      this.stateService.isMaskPIIActive$.subscribe({
        next: (value) => {
          if (this.form) {
            this.form.get('maskPII').setValue(value, { emitEvent: false });
          }
        },
      })
    );
  }

  private subscribeToChromeActions(): void {
    this.subscriptions.push(
      this.stateService.reportDateChangeRequested$.subscribe({
        next: (value) => this.onReportDateChange(value),
      }),
      this.stateService.runCheckRequested$.subscribe({
        next: () => this.runCheck(),
      }),
      this.stateService.downloadRequested$.subscribe({
        next: () => this.download(),
      }),
      this.stateService.activeStateChangeRequested$.subscribe({
        next: () => this.changeActiveState(),
      }),
      this.stateService.sectionSelectRequested$.subscribe({
        next: (value) => this.selectSection(value),
      }),
      this.stateService.deactivateConfirmRequested$.subscribe({
        next: (value) => this.onConfirmDeactivate(value),
      }),
      this.stateService.deactivateCancelRequested$.subscribe({
        next: () => this.onCancelDeactivate(),
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    clearTimeout(this.toastTimer);
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/driver-background-check/driver-background-check.component.ts